import {
    ChangeEvent,
    FC,
    KeyboardEvent,
    MouseEvent,
    ReactNode,
    SVGProps,
    useMemo,
    useState,
} from 'react';
import { Dropdown, Form, Input, Spin } from 'antd';
import type { FormProps } from 'antd';
import type { MenuProps } from 'antd';
import cn from 'classnames';

import FilterIcon from 'components/ui/icons/filter';

import styles from './FilterControls.module.scss';

const DropdownArrowIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg
        {...props}
        width={10}
        height={6}
        viewBox="0 0 10 6"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        focusable="false"
    >
        <path d="M5 6 0 0h10L5 6Z" fill="currentColor" />
    </svg>
);

const SearchIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg
        {...props}
        width={18}
        height={18}
        viewBox="0 0 18 18"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        focusable="false"
    >
        <circle
            cx={8.25}
            cy={8.25}
            r={5.75}
            stroke="currentColor"
            strokeWidth={1.5}
        />
        <path
            d="m12.5 12.5 3 3"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const FilterClearIcon = (props: SVGProps<SVGSVGElement>) => (
    <svg
        {...props}
        width={16}
        height={16}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden
        focusable="false"
    >
        <circle
            cx={8}
            cy={8}
            r={6.25}
            stroke="currentColor"
            strokeWidth={1.5}
        />
        <path
            d="M6.25 6.25 9.75 9.75M9.75 6.25 6.25 9.75"
            stroke="currentColor"
            strokeWidth={1.5}
            strokeLinecap="round"
        />
    </svg>
);

export type FilterDropdownOption = {
    label: ReactNode;
    value: string | number;
    disabled?: boolean;
};

type FilterDropdownProps = {
    placeholder: string;
    value?: string | number;
    options?: FilterDropdownOption[];
    onSelect?: (
        value: string | number | undefined,
        option?: FilterDropdownOption
    ) => void;
    disabled?: boolean;
    clearLabel?: string;
    render?: ReactNode;
    showSearch?: boolean;
    onSearch?: (value: string) => void;
    searchPlaceholder?: string;
    loading?: boolean;
    filterOption?:
        | boolean
        | ((input: string, option: FilterDropdownOption) => boolean);
    debounceMs?: number;
};

type FilterControlsProps = {
    formProps: FormProps;
    hiddenFieldNames?: string[];
    searchFieldName?: string;
    searchPlaceholder: string;
    searchSuffixIcon?: ReactNode;
    filtersLabel: string;
    clearFilterLabel: string;
    isAllActive: boolean;
    onResetFilters: () => void;
    dropdowns?: FilterDropdownProps[];
    extraActions?: ReactNode;
    inlineFilters?: ReactNode;
    hideSearchField?: boolean;
    inlineFiltersFullWidth?: boolean;
    showFilterToggle?: boolean;
    dropdownsGrow?: boolean;
};

const FilterDropdown = (props: FilterDropdownProps) => {
    const {
        placeholder,
        value,
        options,
        onSelect,
        disabled,
        clearLabel,
        render,
        showSearch = false,
        onSearch,
        searchPlaceholder,
        loading,
        filterOption,
        debounceMs,
    } = props;

    const isCustomRender = Boolean(render);
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const mergedOptions = useMemo(() => {
        return options ?? [];
    }, [options]);

    const stringifiedValue =
        value !== undefined && value !== null ? String(value) : undefined;

    const selectedOption = useMemo(() => {
        if (!stringifiedValue) return undefined;

        return mergedOptions.find(
            option => String(option.value) === stringifiedValue
        );
    }, [mergedOptions, stringifiedValue]);

    const shouldShowSearch = Boolean(showSearch);

    const resolvedFilterOption = useMemo(() => {
        if (!shouldShowSearch) return false;

        if (filterOption !== undefined) return filterOption;

        if (onSearch) return false;

        return true;
    }, [filterOption, onSearch, shouldShowSearch]);

    const handleSearch = useMemo(() => {
        if (!shouldShowSearch || !onSearch) return undefined;

        const delay = debounceMs ?? 300;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        return (nextValue: string) => {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }

            timeoutId = setTimeout(() => {
                onSearch(nextValue);
            }, delay);
        };
    }, [debounceMs, onSearch, shouldShowSearch]);

    const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextValue = event.target.value;
        setSearchValue(nextValue);
        handleSearch?.(nextValue);
    };

    const filteredOptions = useMemo(() => {
        const normalizedSearch = searchValue.trim();

        if (
            !shouldShowSearch ||
            normalizedSearch === '' ||
            resolvedFilterOption === false
        ) {
            return mergedOptions;
        }

        if (typeof resolvedFilterOption === 'function') {
            return mergedOptions.filter(option =>
                resolvedFilterOption(normalizedSearch, option)
            );
        }

        const loweredSearch = normalizedSearch.toLowerCase();

        return mergedOptions.filter(option => {
            const labelText =
                typeof option.label === 'string' ||
                typeof option.label === 'number'
                    ? String(option.label)
                    : '';
            const valueText = String(option.value);

            return (
                labelText.toLowerCase().includes(loweredSearch) ||
                valueText.toLowerCase().includes(loweredSearch)
            );
        });
    }, [mergedOptions, resolvedFilterOption, searchValue, shouldShowSearch]);

    const isActive =
        stringifiedValue !== undefined &&
        stringifiedValue !== '' &&
        !!selectedOption;

    const dropdownItems = useMemo<MenuProps['items']>(() => {
        return filteredOptions.map(option => {
            const isSelected =
                stringifiedValue !== undefined &&
                String(option.value) === stringifiedValue;

            return {
                key: String(option.value),
                label: option.label,
                className: cn(styles.filterDropdownItem, {
                    [styles.filterDropdownItemActive]: isSelected,
                }),
                disabled: option.disabled,
            };
        });
    }, [filteredOptions, stringifiedValue]);

    const handleMenuClick: MenuProps['onClick'] = ({ key }) => {
        const option = mergedOptions.find(item => String(item.value) === key);

        if (!option) {
            setOpen(false);
            return;
        }

        onSelect?.(option.value, option);

        setOpen(false);
    };

    if (isCustomRender && render) {
        return <div className={styles.filterCustom}>{render}</div>;
    }

    const dropdownMenu: MenuProps = {
        items: dropdownItems,
        onClick: handleMenuClick,
    };

    const computedPlaceholder = isActive ? selectedOption?.label : placeholder;

    const dropdownRender = shouldShowSearch
        ? (menu: ReactNode) => (
              <div className={styles.filterDropdownContent}>
                  <div className={styles.filterDropdownSearch}>
                      <Input
                          allowClear
                          size="small"
                          placeholder={searchPlaceholder ?? placeholder}
                          value={searchValue}
                          onChange={handleSearchChange}
                          className={styles.filterDropdownSearchInput}
                      />
                  </div>
                  {loading && filteredOptions.length === 0 ? (
                      <div className={styles.filterDropdownLoading}>
                          <Spin size="small" />
                      </div>
                  ) : (
                      menu
                  )}
              </div>
          )
        : undefined;

    const hasEnabledOptions =
        mergedOptions.length > 0 &&
        !mergedOptions.every(opt => opt.disabled);

    const isDropdownDisabled = disabled || (!shouldShowSearch && !hasEnabledOptions);

    const clearSelection = () => {
        onSelect?.(undefined, undefined);
        setOpen(false);
    };

    const handleClearClick = (event: MouseEvent<HTMLSpanElement>) => {
        event.preventDefault();
        event.stopPropagation();
        clearSelection();
    };

    const handleClearKeyDown = (event: KeyboardEvent<HTMLSpanElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;

        event.preventDefault();
        event.stopPropagation();
        clearSelection();
    };

    return (
        <Dropdown
            trigger={['click']}
            open={open}
            onOpenChange={visible => {
                if (!isDropdownDisabled) {
                    setOpen(visible);
                }
            }}
            overlayClassName={styles.filterDropdownMenu}
            menu={dropdownMenu}
            dropdownRender={dropdownRender}
            disabled={isDropdownDisabled}
            getPopupContainer={triggerNode =>
                triggerNode.parentElement ?? document.body
            }
        >
            <button
                type="button"
                className={cn(styles.filterButton, {
                    [styles.filterButtonActive]: isActive,
                    [styles.filterButtonOpen]: open,
                    [styles.filterButtonDisabled]: isDropdownDisabled,
                })}
                aria-haspopup="menu"
                aria-expanded={open}
                disabled={isDropdownDisabled}
            >
                <span
                    className={cn(styles.filterButtonText, {
                        [styles.filterButtonPlaceholder]: !isActive,
                    })}
                >
                    {computedPlaceholder}
                </span>
                {!isActive ? (
                    <DropdownArrowIcon className={styles.dropdownArrow} />
                ) : (
                    <span
                        role="button"
                        tabIndex={0}
                        aria-label={clearLabel}
                        className={styles.filterButtonClear}
                        onClick={handleClearClick}
                        onKeyDown={handleClearKeyDown}
                    >
                        <FilterClearIcon
                            className={styles.filterButtonClearIcon}
                        />
                    </span>
                )}
            </button>
        </Dropdown>
    );
};

export const FilterControls: FC<FilterControlsProps> = ({
    formProps,
    hiddenFieldNames = [],
    searchFieldName = 'query',
    searchPlaceholder,
    searchSuffixIcon,
    filtersLabel,
    clearFilterLabel,
    isAllActive,
    onResetFilters,
    dropdowns = [],
    extraActions,
    inlineFilters,
    hideSearchField = false,
    inlineFiltersFullWidth = false,
    showFilterToggle = true,
    dropdownsGrow = true,
}) => {
    return (
        <Form {...formProps} layout="vertical" className={styles.searchForm}>
            {hiddenFieldNames.map(name => (
                <Form.Item key={name} name={name} hidden>
                    <Input />
                </Form.Item>
            ))}
            {!hideSearchField && (
                <Form.Item
                    name={searchFieldName}
                    className={styles.searchField}
                    label={false}
                >
                    <Input
                        allowClear
                        size="large"
                        placeholder={searchPlaceholder}
                        suffix={
                            searchSuffixIcon ?? (
                                <SearchIcon className={styles.searchIcon} />
                            )
                        }
                    />
                </Form.Item>
            )}
            {extraActions ? (
                <div className={styles.extraActions}>{extraActions}</div>
            ) : null}
            <div className={styles.filtersRow}>
                {showFilterToggle && (
                    <button
                        type="button"
                        onClick={onResetFilters}
                        className={cn(styles.filterToggle, {
                            [styles.filterToggleActive]: isAllActive,
                            [styles.filterToggleWithSelection]: !isAllActive,
                        })}
                        aria-pressed={isAllActive}
                    >
                        <FilterIcon className={styles.filterToggleIcon} />
                        <span className={styles.filterToggleLabel}>
                            {filtersLabel}
                        </span>
                    </button>
                )}
                {dropdowns.length ? (
                    <div
                        className={cn(styles.filtersGroup, {
                            [styles.filtersGroupNoGrow]: !dropdownsGrow,
                        })}
                    >
                        {dropdowns.map((dropdown, index) => (
                            <div className={styles.filterItem} key={index}>
                                <FilterDropdown
                                    {...dropdown}
                                    clearLabel={
                                        dropdown.clearLabel ?? clearFilterLabel
                                    }
                                />
                            </div>
                        ))}
                    </div>
                ) : null}
                {inlineFilters ? (
                    <div className={styles.inlineFilters}>
                        {inlineFiltersFullWidth ? (
                            inlineFilters
                        ) : (
                            <div className={styles.filtersGroup}>
                                <div className={styles.filterItem}>
                                    {inlineFilters}
                                </div>
                            </div>
                        )}
                    </div>
                ) : null}
            </div>
        </Form>
    );
};

export type { FilterControlsProps };
